import React, { useEffect } from 'react';
import Select, { components as ReactSelectComponents, createFilter } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import Icon from '../Icon/Icon';
import useSelectCustomizable, {
  composeAccessibleName,
  getOptionAccessibleText,
  getOptionDisplayText,
  getPrimitiveText,
  resolveOptionText,
} from './useSelectCustomizable';
import type { OptionTextSource } from './useSelectCustomizable';

export type SelectCustomizableOption = {
  /** Lo que se pinta en la opción. Admite JSX; si lo es, pasa también `ariaLabel`. */
  label: React.ReactNode;
  value: unknown;
  /** Texto que anuncia una tecnología de apoyo. No se pinta, pero sí se indexa para la búsqueda.
   *  Con `label` string el nombre accesible se compone ("Matemáticas, Asignatura troncal") para
   *  contener la etiqueta visible (WCAG 2.1 · 2.5.3); con `label` JSX lo sustituye. Si falta y el
   *  `label` es JSX, el texto se deduce del JSX y se avisa por consola en desarrollo. */
  ariaLabel?: string;
  shortLabel?: string;
  __isNew__?: boolean;
};

type ActionMeta = { action: string; removedValue?: SelectCustomizableOption };

export type SelectCustomizableProps = {
  canCreate?: boolean;
  /** Overrides de `components` de react-select. Se FUSIONAN slot a slot con los componentes
   *  accesibles del DS (no los sustituyen). Un slot sobrescrito asume su propia semántica
   *  ARIA (p. ej. un `Option` propio debe poner `role="option"` + `aria-selected`). */
  components?: Record<string, React.ElementType>;
  defaultValue?: unknown;
  error?: string;
  hideSelectedOptions?: boolean;
  iconTypeIndicator?: string;
  inputId?: string;
  isMulti?: boolean;
  isSearchable?: boolean;
  label?: React.ReactNode;
  menuPortalTarget?: HTMLElement | null;
  menuPosition?: 'fixed' | 'absolute';
  /** Cómo se muestran los valores del multi: 'inline' (pills de react-select)
   *  o 'sortedList' (lista <ul> de tags ordenada alfabéticamente). Solo aplica con isMulti. */
  multiValueDisplay?: 'inline' | 'sortedList';
  name?: string;
  noOptionsMessage?: string;
  onChange?: (value: unknown, actionMeta?: ActionMeta) => void;
  onCreateOption?: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  onMenuClose?: () => void;
  onMenuOpen?: () => void;
  options?: SelectCustomizableOption[];
  placeholder?: React.ReactNode;
  required?: boolean;
  /** Muestra los valores del multi como un resumen de una sola línea ("T1, T2, T3")
   *  en lugar de pills/lista. Solo aplica con isMulti. */
  showValuesAsText?: boolean;
  size?: 'default' | 'small';
  /** Tamaño de los tags en multiValueDisplay='sortedList'. */
  tagSize?: 'sm' | 'md';
  useShortLabels?: boolean;
  value?: unknown;
  /** 'default' = borde estándar; 'subtle' = borde tenue (divisor, sin sombra). */
  variant?: 'default' | 'subtle';
  // Props adicionales reenviadas a react-select (isDisabled, isLoading,
  // closeMenuOnSelect, components, aria-label, etc.)
  [key: string]: unknown;
};

// react-select v3 tiene tipados genéricos muy complejos; se castea a un
// componente laxo (mismo patrón que SelectCheckboxs con react-picky).
const SelectTyped = Select as unknown as React.ComponentType<Record<string, unknown>>;
const CreatableSelectTyped = CreatableSelect as unknown as React.ComponentType<Record<string, unknown>>;

// Componentes custom definidos a nivel de módulo para mantener identidad de referencia
// entre renders y evitar que react-select los desmonte/remonte en cada keystroke.
// Los datos del componente padre llegan a través de selectProps (react-select pasa
// todas las props desconocidas del <Select> en selectProps de cada custom component).

type ControlProps = {
  children?: React.ReactNode;
  innerProps?: Record<string, unknown>;
  menuIsOpen?: boolean;
};

function AccessibleControl({ children, innerProps, menuIsOpen, ...controlProps }: ControlProps): React.ReactElement {
  return (
    <ReactSelectComponents.Control innerProps={{ ...innerProps }} menuIsOpen={menuIsOpen} {...controlProps}>
      {children}
    </ReactSelectComponents.Control>
  );
}

type SelectPropsBag = {
  menuIsOpen?: boolean;
  inputValue?: string;
  isMulti?: boolean;
  value?: unknown;
  _listboxId?: string;
  _focusedOptionId?: string;
  _onOptionFocusChange?: (id: string) => void;
  _errorId?: string;
  _hasError?: boolean;
  _required?: boolean;
  _removeLabel?: string;
  _valueId?: string;
  showValuesAsList?: boolean;
  showValuesAsText?: boolean;
  useShortLabels?: boolean;
  iconTypeIndicator?: string;
  getOptionLabel?: (data: SelectCustomizableOption) => unknown;
  onChange?: (value: unknown, actionMeta?: ActionMeta) => void;
};

// El valor seleccionado vive en un <div> hermano del input que el lector se salta al
// enfocar, así que se referencia por aria-describedby. Solo mientras ese nodo está
// montado: react-select desmonta el SingleValue en cuanto hay texto de búsqueda, y solo
// lo pinta si cleanValue(value) devuelve elementos — esa regla se replica aquí abajo.
// Un id colgante no rompe axe (aria-valid-attr-value lo deja en "incomplete", nunca en
// violation), pero es una referencia muerta que ningún lector puede resolver.
// El modo no buscable resuelve lo mismo sobre el DOM en useSelectCustomizable (el
// DummyInput no pasa por este componente): si cambias un lado, revisa el otro.
function getDescribedBy(selectProps: SelectPropsBag): string | undefined {
  // Réplica de cleanValue de react-select: un array se filtra de falsy (value={[]} con
  // isMulti={false} pinta el Placeholder, no un SingleValue) y un no-array solo cuenta
  // si es un objeto no nulo.
  const selected = selectProps.value;
  const hasRenderedSingleValue =
    !selectProps.isMulti &&
    !selectProps.inputValue &&
    (Array.isArray(selected) ? selected.some(Boolean) : typeof selected === 'object' && selected !== null);
  const ids = [
    hasRenderedSingleValue ? selectProps._valueId : null,
    selectProps._hasError ? selectProps._errorId : null,
  ].filter(Boolean);
  return ids.length > 0 ? ids.join(' ') : undefined;
}

function AccessibleInput({ selectProps, ...inputProps }: { selectProps: SelectPropsBag }): React.ReactElement {
  return (
    <ReactSelectComponents.Input
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={selectProps.menuIsOpen ? 'true' : 'false'}
      aria-controls={selectProps._listboxId}
      aria-activedescendant={selectProps._focusedOptionId || undefined}
      aria-invalid={selectProps._hasError ? 'true' : undefined}
      aria-describedby={getDescribedBy(selectProps)}
      aria-required={selectProps._required ? 'true' : undefined}
      selectProps={selectProps}
      {...inputProps}
    />
  );
}

type SingleValueProps = {
  children?: React.ReactNode;
  innerProps?: Record<string, unknown>;
  selectProps: SelectPropsBag;
};

function AccessibleSingleValue({ children, innerProps, selectProps, ...singleValueProps }: SingleValueProps): React.ReactElement {
  return (
    <ReactSelectComponents.SingleValue
      innerProps={{ ...innerProps, id: selectProps._valueId }}
      selectProps={selectProps}
      {...singleValueProps}
    >
      {children}
    </ReactSelectComponents.SingleValue>
  );
}

type MenuProps = {
  children?: React.ReactNode;
  innerProps?: Record<string, unknown>;
  selectProps: SelectPropsBag;
};

function AccessibleMenu({ children, innerProps, selectProps, ...menuProps }: MenuProps): React.ReactElement {
  return (
    <ReactSelectComponents.Menu
      innerProps={{
        ...innerProps,
        role: 'listbox',
        id: selectProps._listboxId,
        // Sin esto, un multi se anuncia como lista de selección única.
        'aria-multiselectable': selectProps.isMulti || undefined,
      }}
      selectProps={selectProps}
      {...menuProps}
    >
      {children}
    </ReactSelectComponents.Menu>
  );
}

type OptionProps = {
  children?: React.ReactNode;
  data?: SelectCustomizableOption;
  innerProps?: { id?: string } & Record<string, unknown>;
  isSelected?: boolean;
  isFocused?: boolean;
  selectProps: SelectPropsBag;
};

// Con `label` JSX el nombre accesible sería el textContent pegado del árbol, así que se
// sustituye por el texto resuelto y coincide con lo que suena en la región aria-live.
// Decide la PROCEDENCIA, no el contenido: un `value` ('value'/'none') es un identificador y
// estampar un guid destruiría el nombre correcto que ya pintaba el label. Con 'label' basta
// el textContent, y un aria-label taparía el `formatOptionLabel` del consumidor.
const OPTION_ARIA_LABEL_SOURCES = new Set<OptionTextSource>(['ariaLabel', 'jsx']);

function getOptionAriaLabel(data: SelectCustomizableOption | undefined, selectProps: SelectPropsBag): string | undefined {
  if (!data) return undefined;
  if (!OPTION_ARIA_LABEL_SOURCES.has(resolveOptionText(data).source)) return undefined;

  // Por `selectProps`, no por el resolvedor, para respetar un `getOptionLabel` del consumidor.
  const text = selectProps.getOptionLabel?.(data);
  if (typeof text !== 'string' || !text.trim()) return undefined;

  return composeAccessibleName(data, text);
}

function AccessibleOption({ children, data, innerProps, isSelected, isFocused, selectProps, ...optionProps }: OptionProps): React.ReactElement {
  useEffect(() => {
    if (isFocused && innerProps?.id) {
      selectProps._onOptionFocusChange?.(innerProps.id);
    }
  }, [isFocused, innerProps?.id, selectProps._onOptionFocusChange]);

  const optionAriaLabel = getOptionAriaLabel(data, selectProps);

  return (
    <ReactSelectComponents.Option
      data={data}
      innerProps={{
        ...innerProps,
        role: 'option',
        'aria-selected': isSelected,
        ...(optionAriaLabel && { 'aria-label': optionAriaLabel }),
      }}
      isSelected={isSelected}
      isFocused={isFocused}
      selectProps={selectProps}
      {...optionProps}
    >
      {children}
    </ReactSelectComponents.Option>
  );
}

function DropdownIndicator({ innerProps, selectProps }: { innerProps?: Record<string, unknown>; selectProps: SelectPropsBag }): React.ReactElement {
  return (
    <div className="select-customizable__indicator " {...innerProps}>
      {selectProps.iconTypeIndicator ? (
        <Icon name={selectProps.iconTypeIndicator} size="sm" color="black" />
      ) : (
        <div className="select-customizable__dropdown-chevron" />
      )}
    </div>
  );
}

function MultiValueContainer({ selectProps }: { selectProps: SelectPropsBag }): React.ReactElement | null {
  return selectProps.showValuesAsList || selectProps.showValuesAsText ? null : (undefined as unknown as React.ReactElement);
}

// Ordenar, resumir y rellenar el `title` usa texto VISIBLE: nunca el `ariaLabel`, que el
// usuario no ve —ordenar por él daría un orden que en pantalla parece aleatorio.
type DisplayTextGetter = (option: SelectCustomizableOption) => string;

function getShortDisplayText(option: SelectCustomizableOption): string {
  return option.shortLabel || getOptionDisplayText(option);
}

function getDisplayTextGetter(selectProps: SelectPropsBag): DisplayTextGetter {
  return selectProps.useShortLabels ? getShortDisplayText : getOptionDisplayText;
}

// Un label opaco no da texto visible. Esos van al final: alfabéticamente la cadena vacía gana
// siempre, y una fila sin nombre encabezando la lista parece un fallo de carga.
function compareDisplayText(a: string, b: string): number {
  if (!a || !b) return Number(!a) - Number(!b);
  return a.localeCompare(b);
}

function sortByDisplayLabel(values: SelectCustomizableOption[], getText: DisplayTextGetter): SelectCustomizableOption[] {
  return [...values].sort((a, b) => compareDisplayText(getText(a), getText(b)));
}

// El chip sí acepta el texto de un label string —pinta el `shortLabel`, así que el nombre
// completo aporta—, pero tampoco estampa el `value`: ver `getOptionAriaLabel`.
const CHIP_ARIA_LABEL_SOURCES = new Set<OptionTextSource>(['ariaLabel', 'label', 'jsx']);

function getChipAriaLabel(option: SelectCustomizableOption): string | undefined {
  const { source, text } = resolveOptionText(option);
  if (!CHIP_ARIA_LABEL_SOURCES.has(source) || !text.trim()) return undefined;

  return composeAccessibleName(option, text);
}

type InlineValueChipProps = {
  option: SelectCustomizableOption;
  accessibleLabel: string | undefined;
  displayLabel: React.ReactNode;
  displayText: string;
  removeLabel: string;
  onRemove: (option: SelectCustomizableOption) => void;
};

function InlineValueChip({ option, accessibleLabel, displayLabel, displayText, removeLabel, onRemove }: InlineValueChipProps): React.ReactElement {
  function handleClick(e: React.MouseEvent): void {
    e.stopPropagation();
    onRemove(option);
  }
  // El botón nunca lleva el `value` (sería "Quitar 084dce70-…", un identificador leído en voz
  // alta), pero sí cae a lo PINTADO cuando no hay nombre accesible: dejarlo en un "Quitar"
  // pelado repetido por cada chip los vuelve indistinguibles entre sí (WCAG 4.1.2 / 2.4.6).
  const removeTarget = accessibleLabel || displayText;
  const removeAriaLabel = removeTarget ? `${removeLabel} ${removeTarget}` : removeLabel;
  return (
    <li className="select-customizable__inline-value" aria-label={accessibleLabel}>
      <span className="select-customizable__inline-value-label">{displayLabel}</span>
      <button className="select-customizable__inline-value-remove" onClick={handleClick} type="button" aria-label={removeAriaLabel} />
    </li>
  );
}

type ValueContainerProps = {
  children?: React.ReactNode;
  hasValue?: boolean;
  getValue?: () => SelectCustomizableOption[];
  selectProps: SelectPropsBag;
};

// showValuesAsText: resumen de una sola línea ("T1, T2, T3"), sin control para quitar
// valores individuales desde el propio control (se gestiona abriendo el desplegable).
function ValueSummary({ children, hasValue, getValue, selectProps }: ValueContainerProps): React.ReactElement {
  if (!hasValue) {
    return <div className="select-customizable__value-container select-customizable__value-container--chips">{children}</div>;
  }

  const getText = getDisplayTextGetter(selectProps);
  const sorted = sortByDisplayLabel(getValue?.() ?? [], getText);
  // Los labels opacos no aportan texto: se descartan antes de unir, o el resumen empezaría
  // por una coma suelta (", T1, T3") tanto en pantalla como en el `title`.
  const summary = sorted
    .map(getText)
    .filter(Boolean)
    .join(', ');

  return (
    <div className="select-customizable__value-container select-customizable__value-container--chips">
      <span className="select-customizable__value-summary" title={summary}>
        {summary}
      </span>
      {Array.isArray(children) ? children[1] : null}
    </div>
  );
}

function ValueContainer({ children, hasValue, getValue, selectProps }: ValueContainerProps): React.ReactElement {
  if (selectProps.showValuesAsText) {
    return (
      <ValueSummary hasValue={hasValue} getValue={getValue} selectProps={selectProps}>
        {children}
      </ValueSummary>
    );
  }

  if (!selectProps.showValuesAsList) {
    return children as React.ReactElement;
  }

  if (!hasValue) {
    return (
      <div className="select-customizable__value-container select-customizable__value-container--chips">
        {children}
      </div>
    );
  }

  const values = getValue?.() ?? [];
  const getText = getDisplayTextGetter(selectProps);
  const sorted = sortByDisplayLabel(values, getText);

  function handleRemoveValue(v: SelectCustomizableOption): void {
    const newValues = values.filter((val) => val.value !== v.value);
    selectProps.onChange?.(newValues, { action: 'remove-value', removedValue: v });
  }

  return (
    <div className="select-customizable__value-container select-customizable__value-container--chips">
      <ul className="select-customizable__inline-values">
        {sorted.map((v) => {
          const displayLabel = selectProps.useShortLabels && v.shortLabel ? v.shortLabel : v.label;
          return (
            <InlineValueChip
              key={String(v.value)}
              option={v}
              accessibleLabel={getChipAriaLabel(v)}
              displayLabel={displayLabel}
              displayText={getText(v)}
              removeLabel={selectProps._removeLabel ?? 'Remove'}
              onRemove={handleRemoveValue}
            />
          );
        })}
      </ul>
      {Array.isArray(children) ? children[1] : null}
    </div>
  );
}

// react-select filtra por `${getOptionLabel(option)} ${value}`, y nuestro `getOptionLabel`
// prioriza el `ariaLabel`: sin esto, declarar un `ariaLabel` sacaría el texto VISIBLE del
// índice de búsqueda. El heno lleva las tres pistas, deduplicadas. `createFilter` conserva
// sus defaults (ignoreCase, ignoreAccents, trim, matchFrom).
type FilterableOption = {
  label: string;
  value: unknown;
  data: SelectCustomizableOption;
};

type OptionFilter = (option: FilterableOption, rawInput: string) => boolean;

function stringifyOptionForFilter({ data, label, value }: FilterableOption): string {
  const haystack = [getOptionDisplayText(data), label, getPrimitiveText(value)];
  return haystack.filter((part, index) => part && haystack.indexOf(part) === index).join(' ');
}

const filterOptionByText: OptionFilter = createFilter({ stringify: stringifyOptionForFilter });

export const utilsSelectCustomizable = {
  /**
   * Devuelve las opciones que necesita SelectCustomizable, recorriendo el array
   * elements y accediendo a propertyToLabel para el label y a propertyToValue
   * para el value de cada opción.
   */
  getOptionsProperties(
    elements: Record<string, unknown>[],
    propertyToLabel: string,
    propertyToValue: string
  ): { label: unknown; value: unknown }[] {
    return elements && propertyToLabel && propertyToValue
      ? elements.map((element) => ({
          label: element[propertyToLabel],
          value: element[propertyToValue],
        }))
      : [];
  },
};

/**
 * SelectCustomizable — Select accesible (WCAG AA) basado en react-select, con
 * label/error/required internos, modo creatable, multi con valores en lista
 * ordenada (multiValueDisplay='sortedList'), grupos y soporte de teclado completo.
 */
function SelectCustomizable({
  canCreate,
  components: consumerComponents,
  defaultValue,
  error,
  hideSelectedOptions = false,
  iconTypeIndicator,
  inputId,
  isMulti = false,
  isSearchable,
  label,
  menuPortalTarget,
  menuPosition = 'fixed',
  multiValueDisplay = 'inline',
  name,
  noOptionsMessage,
  onChange = () => null,
  onCreateOption,
  onKeyDown,
  onMenuClose,
  onMenuOpen,
  options = [],
  placeholder,
  required,
  showValuesAsText = false,
  size = 'default',
  tagSize = 'md',
  useShortLabels,
  value,
  variant = 'default',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  ...props
}: Readonly<SelectCustomizableProps>): React.ReactElement {
  const {
    resolvedInputId,
    labelId,
    errorId,
    selectRef,
    finalIsSearchable,
    finalMenuPlacement,
    getNoOptionsMessage,
    getCreateLabel,
    getScreenReaderStatus,
    sharedStyles,
    selectStyles,
    resolvedPlaceholder,
    resolvedAriaLabel,
    labelTitle,
    className,
    isSortedList,
    sharedProps,
    handleCreatableChange,
    formatGroupLabel,
    getOptionLabel,
  } = useSelectCustomizable({
    ariaLabelledBy: ariaLabelledBy as string | undefined,
    inputId,
    name,
    isSearchable,
    error,
    noOptionsMessage,
    label,
    ariaLabel: ariaLabel as string | undefined,
    placeholder,
    required,
    variant,
    size,
    tagSize,
    iconTypeIndicator,
    multiValueDisplay,
    showValuesAsText,
    useShortLabels,
    onChange,
    onCreateOption,
    onKeyDown,
    onMenuOpen,
    onMenuClose,
  });

  return (
    <>
      {label && (
        // htmlFor además del aria-labelledby: la asociación nativa lleva el foco al select
        // al pulsar la etiqueta, como en cualquier campo de formulario. De propina, hace que
        // axe resuelva el label también bajo jsdom, donde los rects 0x0 + un ancestro con
        // overflow (p. ej. el contenido de un Dialog) le hacen descartar el texto "visible"
        // del aria-labelledby (label-title-only).
        <label className="select-customizable__label" htmlFor={resolvedInputId} title={labelTitle} id={labelId}>
          {label} {required && <sup aria-hidden="true">*</sup>}
        </label>
      )}
      {canCreate ? (
        <CreatableSelectTyped
          ref={selectRef}
          menuPosition="fixed"
          menuPlacement={finalMenuPlacement}
          options={options}
          className={className}
          classNamePrefix="select-customizable"
          defaultValue={defaultValue}
          value={value}
          placeholder={resolvedPlaceholder}
          inputId={resolvedInputId}
          aria-labelledby={label ? labelId : ariaLabelledBy}
          aria-label={resolvedAriaLabel}
          components={{
            DropdownIndicator,
            Control: AccessibleControl,
            Input: AccessibleInput,
            Menu: AccessibleMenu,
            Option: AccessibleOption,
            SingleValue: AccessibleSingleValue,
            ...consumerComponents,
          }}
          noOptionsMessage={getNoOptionsMessage}
          screenReaderStatus={getScreenReaderStatus}
          isMulti={isMulti}
          onChange={handleCreatableChange}
          onCreateOption={onCreateOption}
          createOptionPosition="last"
          isClearable
          formatCreateLabel={getCreateLabel}
          name={name}
          // Hacen falta los dos: en cuanto `getOptionLabel` devuelve texto plano, sin
          // `formatOptionLabel` esta rama pintaría ese texto en lugar del JSX del consumidor.
          getOptionLabel={getOptionAccessibleText}
          formatOptionLabel={getOptionLabel}
          filterOption={filterOptionByText}
          styles={sharedStyles}
          {...props}
          {...sharedProps}
        />
      ) : (
        <SelectTyped
          ref={selectRef}
          menuPosition={menuPosition}
          menuPlacement={finalMenuPlacement}
          options={options}
          className={className}
          classNamePrefix={isSortedList ? 'select-customizable--show-values-as-list' : 'select-customizable'}
          defaultValue={defaultValue}
          value={value}
          placeholder={resolvedPlaceholder}
          inputId={resolvedInputId}
          aria-labelledby={label ? labelId : ariaLabelledBy}
          aria-label={resolvedAriaLabel}
          // react-select SUSTITUYE (no fusiona) su mapa de components con el que recibe: si
          // `components` viajara suelto en {...props} (que se extiende después), el mapa del
          // consumidor borraría entero el nuestro — sin AccessibleMenu no hay role="listbox"
          // y el combobox quedaría apuntando a un listbox que no existe jamás. Se fusiona
          // aquí slot a slot para que el consumidor solo reemplace lo que pasa.
          components={{
            DropdownIndicator,
            Control: AccessibleControl,
            Input: AccessibleInput,
            Menu: AccessibleMenu,
            Option: AccessibleOption,
            SingleValue: AccessibleSingleValue,
            ...((isSortedList || showValuesAsText) && {
              MultiValue: MultiValueContainer,
              ValueContainer,
            }),
            ...consumerComponents,
          }}
          noOptionsMessage={getNoOptionsMessage}
          screenReaderStatus={getScreenReaderStatus}
          isMulti={isMulti}
          onChange={onChange}
          name={name}
          menuPortalTarget={menuPortalTarget}
          formatGroupLabel={formatGroupLabel}
          hideSelectedOptions={hideSelectedOptions}
          // `getOptionLabel` = texto para el lector, `formatOptionLabel` = lo que se pinta.
          // Antes de los spreads a propósito, para que un consumidor pueda sobrescribirlos.
          getOptionLabel={getOptionAccessibleText}
          formatOptionLabel={getOptionLabel}
          filterOption={filterOptionByText}
          styles={selectStyles}
          isSearchable={finalIsSearchable}
          {...props}
          {...sharedProps}
        />
      )}
      {error && (
        <span className="select-customizable__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </>
  );
}

export default SelectCustomizable;
